import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, Calendar, AlertCircle, Plus, Edit3, X, Trash2 } from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../theme';
import { SectionCard, StatCard, StatusPill, TabBar } from '../components/swipe';

// NABH Committee Meetings — tracks the 5 mandatory committees, their
// meeting minutes, attendance, and action-item follow-ups. The dashboard
// surfaces any committee that hasn't met in >60 days so the clinic can
// schedule before the assessor notices.
export default function CommitteeMeetingsPage({ API_BASE, currentUser }) {
  const [dash, setDash] = useState(null);
  const [types, setTypes] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activeCommittee, setActiveCommittee] = useState('all');
  const [tab, setTab] = useState('dashboard');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t, m] = await Promise.all([
        axios.get(`${API_BASE}/committees/dashboard`),
        axios.get(`${API_BASE}/committees/types`),
        axios.get(`${API_BASE}/committees`),
      ]);
      setDash(d.data); setTypes(t.data); setMeetings(m.data);
    } catch (e) { console.warn(e); }
    setLoading(false);
  }, [API_BASE]);
  useEffect(() => { load(); }, [load]);

  const saveMeeting = async (payload) => {
    try {
      if (payload._id) await axios.put(`${API_BASE}/committees/${payload._id}`, payload);
      else             await axios.post(`${API_BASE}/committees`, payload);
      setEditing(null);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting record?')) return;
    await axios.delete(`${API_BASE}/committees/${id}`);
    load();
  };

  const filtered = activeCommittee === 'all' ? meetings : meetings.filter(m => m.committee === activeCommittee);

  if (loading && !dash) return <div style={{ padding: 40, color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.4rem', fontWeight: 700 }}>Committee Meetings</h2>
          <p style={{ margin: '2px 0 0', color: theme.textMuted, fontSize: '0.86rem' }}>
            NABH-mandated committee minutes, attendance, and action-item tracking
          </p>
        </div>
        <button onClick={() => setEditing({ date: new Date().toISOString().slice(0,10), committee: 'infection-control', status: 'scheduled', agenda: [], attendees: [], actionItems: [] })} style={primaryBtn}>
          <Plus size={14}/> Schedule Meeting
        </button>
      </div>

      {/* Top stats */}
      {dash && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
          <StatCard label="Committees"        value={dash.committees.length}       tone="navy" icon={<Users size={22}/>} />
          <StatCard label="Overdue (>60 days)" value={dash.overdueCount}           tone="danger" sub="Needs scheduling" icon={<AlertCircle size={22}/>} />
          <StatCard label="Total Meetings"    value={meetings.length}              tone="success" icon={<Calendar size={22}/>} />
          <StatCard label="Open Actions"      value={dash.totalOpenActions}        tone="warning" sub="Across all committees" />
        </div>
      )}

      <TabBar
        tabs={[
          { key: 'dashboard', label: 'Committees' },
          { key: 'meetings',  label: `Meetings`, count: filtered.length },
        ]}
        active={tab}
        onChange={setTab}
        variant="underline"
      />

      {/* Dashboard: committee cards */}
      {tab === 'dashboard' && dash && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {dash.committees.map(c => (
            <div key={c.code} style={{
              padding: 18, backgroundColor: theme.surface, border: `1px solid ${c.overdue ? theme.danger + '40' : theme.border}`,
              borderRadius: theme.radiusLg, boxShadow: theme.shadow,
              borderLeft: `4px solid ${c.overdue ? theme.danger : c.meetingCount > 0 ? theme.success : theme.textMuted}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: 0, color: theme.navy, fontSize: '1rem', fontWeight: 700 }}>{c.name}</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: theme.textMuted }}>
                    {c.meetingCount === 0 ? 'Never met' :
                     c.daysSinceLast === 0 ? 'Met today' :
                     c.daysSinceLast === 1 ? 'Met yesterday' :
                     `Last met ${c.daysSinceLast} days ago`}
                  </p>
                </div>
                {c.overdue && <StatusPill preset="danger" label="OVERDUE" size="sm"/>}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12, fontSize: '0.8rem', flexWrap: 'wrap' }}>
                <div style={pillStyle}><strong>{c.meetingCount}</strong> meetings</div>
                {c.openActions > 0 && <div style={{ ...pillStyle, backgroundColor: theme.warning + '18', color: theme.warning }}><strong>{c.openActions}</strong> open actions</div>}
              </div>
              <button onClick={() => { setActiveCommittee(c.code); setTab('meetings'); }} style={{ marginTop: 12, ...ghostBtn, width: '100%' }}>View meetings →</button>
            </div>
          ))}
        </div>
      )}

      {/* Meetings list */}
      {tab === 'meetings' && (
        <SectionCard variant="white" padding="0">
          <div style={{ padding: 12, borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.84rem', color: theme.textMuted, marginRight: 6 }}>Committee:</span>
            <button onClick={() => setActiveCommittee('all')} style={activeCommittee === 'all' ? chipActive : chipIdle}>All</button>
            {types.map(t => (
              <button key={t.code} onClick={() => setActiveCommittee(t.code)} style={activeCommittee === t.code ? chipActive : chipIdle}>{t.name}</button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>No meetings yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Committee</th>
                  <th style={th}>Chair</th>
                  <th style={th}>Attendees</th>
                  <th style={th}>Actions</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => {
                  const done = (m.actionItems || []).filter(a => a.status === 'done').length;
                  const total = (m.actionItems || []).length;
                  return (
                    <tr key={m._id}>
                      <td style={td}>{new Date(m.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td style={td}>{m.committeeName}</td>
                      <td style={td}>{m.chair || '—'}</td>
                      <td style={td}>{(m.attendees || []).filter(a => a.present !== false).length} / {(m.attendees || []).length}</td>
                      <td style={td}>{total ? `${done}/${total} done` : '—'}</td>
                      <td style={td}><StatusPill
                        preset={m.status === 'closed' ? 'success' : m.status === 'in_progress' ? 'warning' : 'info'}
                        label={m.status?.replace('_',' ')} size="sm"/></td>
                      <td style={td}>
                        <button onClick={() => setEditing(m)} style={iconBtn}><Edit3 size={12}/></button>
                        <button onClick={() => deleteMeeting(m._id)} style={{ ...iconBtn, color: theme.danger }}><Trash2 size={12}/></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SectionCard>
      )}

      {editing && <MeetingEditor meeting={editing} types={types} currentUser={currentUser} onSave={saveMeeting} onClose={() => setEditing(null)}/>}
    </div>
  );
}

function MeetingEditor({ meeting, types, currentUser, onSave, onClose }) {
  const [m, setM] = useState({ ...meeting });
  const update = (patch) => setM(prev => ({ ...prev, ...patch }));

  const addAgenda = () => update({ agenda: [...(m.agenda || []), ''] });
  const updAgenda = (i, v) => update({ agenda: m.agenda.map((x, idx) => idx === i ? v : x) });
  const rmAgenda  = (i)    => update({ agenda: m.agenda.filter((_, idx) => idx !== i) });

  const addAttendee = () => update({ attendees: [...(m.attendees || []), { name: '', role: '', present: true }] });
  const updAttendee = (i, patch) => update({ attendees: m.attendees.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
  const rmAttendee  = (i) => update({ attendees: m.attendees.filter((_, idx) => idx !== i) });

  const addAction = () => update({ actionItems: [...(m.actionItems || []), { task: '', assignedTo: '', dueDate: '', status: 'pending' }] });
  const updAction = (i, patch) => update({ actionItems: m.actionItems.map((x, idx) => idx === i ? { ...x, ...patch } : x) });
  const rmAction  = (i) => update({ actionItems: m.actionItems.filter((_, idx) => idx !== i) });

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={sheet}>
        <div style={sheetHead}>
          <h3 style={{ margin: 0, color: theme.navy }}>{m._id ? 'Edit' : 'Schedule'} Meeting</h3>
          <button onClick={onClose} style={iconBtn}><X size={18}/></button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', maxHeight: 'calc(85vh - 60px)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Committee *</label>
              <select value={m.committee} onChange={e => update({ committee: e.target.value, committeeName: types.find(t => t.code === e.target.value)?.name || '' })} style={inputStyle}>
                {types.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Date *</label>
              <input type="date" value={m.date?.slice(0,10) || ''} onChange={e => update({ date: e.target.value })} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Venue</label>
              <input value={m.venue || ''} onChange={e => update({ venue: e.target.value })} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Chair</label>
              <input value={m.chair || ''} onChange={e => update({ chair: e.target.value })} style={inputStyle}/>
            </div>
          </div>

          <label style={{ ...labelStyle, marginTop: 16 }}>Agenda</label>
          {(m.agenda || []).map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input value={a} onChange={e => updAgenda(i, e.target.value)} style={inputStyle} placeholder={`Item ${i+1}`}/>
              <button onClick={() => rmAgenda(i)} style={iconBtn}><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={addAgenda} style={{ ...ghostBtn, fontSize: '0.82rem' }}><Plus size={12}/> Agenda item</button>

          <label style={{ ...labelStyle, marginTop: 16 }}>Discussion / Minutes</label>
          <textarea value={m.discussion || ''} onChange={e => update({ discussion: e.target.value })} rows={4} style={{ ...inputStyle, resize: 'vertical' }}/>

          <label style={{ ...labelStyle, marginTop: 16 }}>Attendees</label>
          {(m.attendees || []).map((a, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 60px 30px', gap: 6, marginBottom: 6 }}>
              <input value={a.name} onChange={e => updAttendee(i, { name: e.target.value })} style={inputStyle} placeholder="Name"/>
              <input value={a.role} onChange={e => updAttendee(i, { role: e.target.value })} style={inputStyle} placeholder="Role"/>
              <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={a.present !== false} onChange={e => updAttendee(i, { present: e.target.checked })}/>
                Present
              </label>
              <button onClick={() => rmAttendee(i)} style={iconBtn}><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={addAttendee} style={{ ...ghostBtn, fontSize: '0.82rem' }}><Plus size={12}/> Attendee</button>

          <label style={{ ...labelStyle, marginTop: 16 }}>Action Items</label>
          {(m.actionItems || []).map((a, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1.4fr 1fr 1fr 30px', gap: 6, marginBottom: 6 }}>
              <input value={a.task}        onChange={e => updAction(i, { task: e.target.value })}        style={inputStyle} placeholder="Task"/>
              <input value={a.assignedTo}  onChange={e => updAction(i, { assignedTo: e.target.value })}  style={inputStyle} placeholder="Owner"/>
              <input type="date" value={a.dueDate?.slice(0,10) || ''} onChange={e => updAction(i, { dueDate: e.target.value })} style={inputStyle}/>
              <select value={a.status} onChange={e => updAction(i, { status: e.target.value })} style={inputStyle}>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
                <option value="deferred">Deferred</option>
              </select>
              <button onClick={() => rmAction(i)} style={iconBtn}><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={addAction} style={{ ...ghostBtn, fontSize: '0.82rem' }}><Plus size={12}/> Action item</button>

          <label style={{ ...labelStyle, marginTop: 16 }}>Meeting Status</label>
          <select value={m.status} onChange={e => update({ status: e.target.value })} style={inputStyle}>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>

          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={ghostBtn}>Cancel</button>
            <button onClick={() => onSave(m)} style={primaryBtn}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── styles ──
const primaryBtn = { padding: '9px 16px', backgroundColor: theme.primary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' };
const ghostBtn   = { padding: '7px 14px', backgroundColor: theme.surface, color: theme.navy, border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 };
const iconBtn    = { padding: '5px 7px', backgroundColor: 'transparent', color: theme.navy, border: 'none', borderRadius: 6, cursor: 'pointer' };
const pillStyle  = { padding: '4px 10px', borderRadius: 12, backgroundColor: '#f1f5f9', color: theme.navy };
const chipIdle   = { padding: '5px 12px', borderRadius: 14, backgroundColor: '#eef0f3', color: theme.textMuted, border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 };
const chipActive = { ...chipIdle, backgroundColor: theme.primary, color: '#fff' };
const td = { padding: '10px 14px', borderBottom: `1px solid ${theme.borderLight}`, fontSize: '0.86rem', color: theme.navy };
const th = { padding: '11px 14px', borderBottom: `1px solid ${theme.border}`, color: theme.textMuted, textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', backgroundColor: '#fafbfc' };
const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const sheet = { backgroundColor: '#fff', borderRadius: 14, width: '95%', maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const sheetHead = { padding: '16px 20px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
